import { autocompletion, type CompletionContext, type CompletionSource } from '@codemirror/autocomplete';
import { type Extension, RangeSetBuilder } from '@codemirror/state';
import { Decoration, type DecorationSet, EditorView, ViewPlugin, type ViewUpdate } from '@codemirror/view';
import type { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import CodeMirrorEditor from 'components/editors/CodeMirrorEditor';
import { type FC, type Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { analyzePromptTags, promptTagColor } from './promptTags';

interface PromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  /** Focus the editor on mount (used when entering edit mode). */
  autoFocus?: boolean;
  /** Ref to the CodeMirror instance, exposing `.view` for cursor-aware tag insertion. */
  editorRef?: Ref<ReactCodeMirrorRef>;
  /** Sections available to insert by their human-readable name. */
  promptSections?: PromptSectionOption[];
  /** Receives the editor's selected text so contextual actions can reflect it. */
  onSelectionChange?: (selectedText: string) => void;
  /** Opens a prompt section when its inline reference is clicked. */
  onPromptSectionClick?: (sectionName: string) => void;
}

export interface PromptSectionOption {
  name: string;
  displayName: string;
}

/**
 * Advanced prompt editor: the shared {@link CodeMirrorEditor} base (Relif design system,
 * line numbers, search, soft wrapping) plus Relif prompt-tag syntax highlighting. The
 * tag-colouring extension lives here because it is specific to the prompt grammar.
 */

// Reuse one Decoration per (colour, weight) so repeated tags don't allocate fresh specs.
const tagMarkCache = new Map<string, Decoration>();
const tagMark = (color: string, bold: boolean): Decoration => {
  const key = `${color}|${bold}`;
  const cached = tagMarkCache.get(key);
  if (cached) return cached;
  const deco = Decoration.mark({ attributes: { style: `color:${color}${bold ? ';font-weight:700' : ''}` } });
  tagMarkCache.set(key, deco);
  return deco;
};

const sectionTagMarkCache = new Map<string, Decoration>();
const sectionTagMark = (color: string, bold: boolean, sectionName: string): Decoration => {
  const key = `${color}|${bold}|${sectionName}`;
  const cached = sectionTagMarkCache.get(key);
  if (cached) return cached;
  const deco = Decoration.mark({
    attributes: {
      class: 'cm-prompt-section-tag',
      'data-prompt-section-name': sectionName,
      title: 'Editar sección',
      style: `color:${color};cursor:pointer;text-decoration:underline;text-decoration-style:dotted${
        bold ? ';font-weight:700' : ''
      }`,
    },
  });
  sectionTagMarkCache.set(key, deco);
  return deco;
};

// Colour every <TAG> with the shared rules (value tags → orange, conditional tags →
// depth palette, invalid tags → red) so the editor matches the Markdown preview's
// colours. The tag keyword (the word between `<` and `:`, e.g. IF / SECTION / CLIENT)
// is bolded so the tag type stands out while editing. Depth/validity need the whole
// document, so we analyse it in full rather than per visible range — prompt sizes make
// this cheap.
function buildTagDecorations(view: EditorView): DecorationSet {
  const doc = view.state.doc.toString();
  const { depths, invalid } = analyzePromptTags(doc);
  const builder = new RangeSetBuilder<Decoration>();
  let i = 0;
  for (const m of doc.matchAll(/<[^<>]+>/g)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    const color = promptTagColor(m[0], depths[i], invalid[i]);
    const colon = m[0].indexOf(':');
    const sectionName = m[0].match(/^<SECTION:([^>]+)>$/)?.[1];
    const mark = (bold: boolean) => (sectionName ? sectionTagMark(color, bold, sectionName) : tagMark(color, bold));
    if (colon >= 0) {
      // Bold the keyword (`<IF`, `</SECTION`…) up to the colon; leave the attribute path plain.
      const splitAt = start + colon;
      builder.add(start, splitAt, mark(true));
      builder.add(splitAt, end, mark(false));
    } else {
      // No attribute path (e.g. <WORKING_HOURS>) — the whole tag is the keyword.
      builder.add(start, end, mark(true));
    }
    i++;
  }
  return builder.finish();
}

const placeholderHighlight = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = buildTagDecorations(view);
    }

    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildTagDecorations(update.view);
      }
    }
  },
  {
    decorations: (instance) => instance.decorations,
  }
);

const sectionCompletionSource =
  (sections: PromptSectionOption[]): CompletionSource =>
  (context: CompletionContext) => {
    // `<` is the quick trigger. From there, users filter by the visible section
    // name (for example, "Información del Agente"), never by its internal tag.
    const match = context.matchBefore(/<[^>\n]*/);
    if (!match) return null;

    return {
      // Preserve the typed `<`; selecting an item fills the rest of the syntax.
      from: match.from + 1,
      options: sections.map((section) => ({
        label: section.displayName || section.name,
        detail: section.displayName ? section.name : undefined,
        type: 'keyword',
        apply: (view, _completion, replaceFrom, replaceTo) => {
          const insertion = `SECTION:${section.name}>`;
          view.dispatch({
            changes: { from: replaceFrom, to: replaceTo, insert: insertion },
            selection: { anchor: replaceFrom + insertion.length },
          });
        },
      })),
    };
  };

// The editor holds the document while typing and only lifts it to the consumer once typing
// settles: consumers re-render large trees per change, which cost tens of milliseconds of
// main-thread time per character. Leaving the editor lifts immediately, so anything reading
// the prompt after a click still sees the final text.
const LIFT_DELAY_MS = 600;

const PromptEditor: FC<PromptEditorProps> = ({
  value,
  onChange,
  ariaLabel,
  autoFocus,
  editorRef,
  promptSections = [],
  onSelectionChange,
  onPromptSectionClick,
}) => {
  const [doc, setDoc] = useState(value);
  // Tells our own lifted value apart from a genuine external one (a restored version),
  // which must replace the document.
  const liftedRef = useRef(value);
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const pending = pendingRef.current;
    if (pending === null) return;
    pendingRef.current = null;
    liftedRef.current = pending;
    onChange(pending);
  }, [onChange]);

  // Read through a ref so the extensions below never need to be rebuilt.
  const flushRef = useRef(flush);
  flushRef.current = flush;

  useEffect(() => {
    if (value === liftedRef.current) return;
    liftedRef.current = value;
    pendingRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    setDoc(value);
  }, [value]);

  useEffect(() => () => flushRef.current(), []);

  const handleChange = useCallback((next: string) => {
    setDoc(next);
    pendingRef.current = next;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flushRef.current(), LIFT_DELAY_MS);
  }, []);

  const promptExtensions = useMemo<Extension[]>(
    () => [
      placeholderHighlight,
      EditorView.updateListener.of((update) => {
        if (!onSelectionChange || (!update.selectionSet && !update.docChanged)) return;
        const { from, to } = update.state.selection.main;
        onSelectionChange(update.state.doc.sliceString(from, to));
      }),
      EditorView.domEventHandlers({
        // Leaving the editor lifts immediately, so anything that reads the prompt after a
        // click (saving, navigating away) sees the final text instead of the debounced one.
        blur: () => {
          flushRef.current();
          return false;
        },
        mousedown: (event) => {
          if (!onPromptSectionClick) return false;
          const target = event.target as HTMLElement;
          const tag = target.closest<HTMLElement>('[data-prompt-section-name]');
          const sectionName = tag?.dataset.promptSectionName;
          if (!sectionName) return false;
          // CodeMirror changes the selection on mousedown. Handle the tag first
          // so opening its modal leaves the editor value, caret and selection intact.
          event.preventDefault();
          event.stopPropagation();
          onPromptSectionClick(sectionName);
          return true;
        },
        click: (event) => {
          const target = event.target as HTMLElement;
          if (!target.closest('[data-prompt-section-name]')) return false;
          event.preventDefault();
          event.stopPropagation();
          return true;
        },
      }),
      autocompletion({
        override: [sectionCompletionSource(promptSections)],
        activateOnTyping: true,
        activateOnTypingDelay: 0,
      }),
    ],
    [onPromptSectionClick, onSelectionChange, promptSections]
  );

  return (
    <CodeMirrorEditor
      value={doc}
      onChange={handleChange}
      extensions={promptExtensions}
      height="100%"
      autoFocus={autoFocus}
      ariaLabel={ariaLabel}
      editorRef={editorRef}
    />
  );
};

export default PromptEditor;
